// The landing page for roles without the business-overview dashboard
// (technicians, receiving desk, runners, B2B users …): who you are, where
// your scope ends, and every page your role owns — one card each.
import { Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { PageHeader } from "@/components/common/PageHeader";
import { navFor } from "@/lib/nav";
import { ArrowRight, MapPin } from "lucide-react";

const ACTION_LABEL = { VIEW: "View", CREATE: "Create", EDIT: "Edit", APPROVE: "Approve", CANCEL: "Cancel", PRINT: "Print", EXPORT: "Export", ASSIGN: "Assign", RECEIVE: "Receive", VERIFY: "Verify" };

export function scopeLine(session) {
  const n = session?.scopeNames || {};
  const parts = [n.city, n.hub, n.center, n.department, n.zone, n.b2bPartner, n.outsourceLab, n.radiologyPartner].filter(Boolean);
  return parts.length ? parts.join(" › ") : session?.scope?.label || "Whole network";
}

export default function RoleHome() {
  const { session, allowed } = useApp();
  const groups = navFor(allowed, session).filter((g) => g.section && g.section !== "Help & Support");
  const first = (session?.name || "").split(" ")[0];
  return (
    <>
      <PageHeader
        breadcrumb={`${session?.roleLabel} · ${session?.roleFamily || ""}`}
        title={`Welcome, ${first}`}
        subtitle={session?.roleSpec || "Your role decides which pages and actions you see."}
      />
      <div className="mb-6 flex flex-wrap items-center gap-2 text-sm" data-testid="role-home-scope">
        <span className="inline-flex items-center gap-1.5 rounded-full border bg-card px-3 py-1 font-medium"><MapPin className="h-3.5 w-3.5 text-primary" /> {scopeLine(session)}</span>
        <span className="text-xs text-muted-foreground">{session?.scope?.label}</span>
      </div>

      <div className="space-y-6" data-testid="role-home">
        {groups.map((g) => (
          <section key={g.section}>
            <h2 className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.06em] text-primary">{g.icon && <g.icon className="h-4 w-4" />} {g.section}</h2>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {g.items.map((item) => {
                const Icon = item.icon;
                const actions = (session?.permissions?.[item.mod] || []).map((a) => ACTION_LABEL[a] || a);
                return (
                  <Link key={item.path} to={item.path} data-testid={`home-card-${item.mod}`}
                    className="group card-elev flex items-start gap-3 rounded-lg border border-border bg-card p-4 transition-all duration-150 hover:border-primary/50 hover:shadow-md active:scale-[0.99]">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary"><Icon className="h-4.5 w-4.5" /></span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-1 font-semibold">{item.label} <ArrowRight className="h-3.5 w-3.5 opacity-0 transition-all group-hover:translate-x-0.5 group-hover:opacity-100" /></span>
                      <span className="mt-0.5 block text-xs text-muted-foreground">{actions.join(" · ")}</span>
                    </span>
                  </Link>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </>
  );
}
